import { useAutoneTranslation } from '@autone/translations';
import { useIsFirstRender } from '@autone/utils';
import { ChevronDown } from 'lucide-react';
import {
  forwardRef,
  type MouseEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  type PopoverContentProps,
} from '../../../atoms';
import { cn } from '../../../utils';
import { type AutoneFilterChipBadge } from '../../core-general-filters';

import {
  AutoneScopeDropdownBadges,
  type AutoneScopeDropdownBadgesRef,
} from './AutoneScopeDropdownBadges';

type InteractOutsideEvent = Parameters<
  NonNullable<PopoverContentProps['onPointerDownOutside']>
>[0];

interface AutoneScopeDropdownProps {
  id: string;
  search: string;
  onSearchChange: (value: string) => void;
  className?: string;
  dropdownContent: React.ReactNode;
  badgeList: AutoneFilterChipBadge[];
  onClearAll: () => void;
}

export const AutoneScopeDropdown = forwardRef<
  HTMLInputElement,
  AutoneScopeDropdownProps
>(
  (
    {
      id,
      search,
      onSearchChange,
      className,
      dropdownContent,
      badgeList,
      onClearAll,
    },
    ref,
  ) => {
    const { common } = useAutoneTranslation();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const badgeRefs = useRef<AutoneScopeDropdownBadgesRef>(new Map());

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const handlePointerDownOutside = useCallback((e: InteractOutsideEvent) => {
      if (anchorRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    }, []);

    const isClickOnBadge = useCallback((target: EventTarget) => {
      for (const badgeEl of badgeRefs.current.values()) {
        if (badgeEl?.contains(target as Node)) return true;
      }
      return false;
    }, []);

    const handlePopoverAnchorClick = useCallback(
      (e: MouseEvent<HTMLDivElement>) => {
        if (isClickOnBadge(e.target)) return;
        inputRef.current?.focus();
      },
      [isClickOnBadge],
    );

    const handlePopoverAnchorMouseDown = useCallback(
      (e: MouseEvent<HTMLDivElement>) => {
        if (isClickOnBadge(e.target)) e.preventDefault();
      },
      [isClickOnBadge],
    );

    const wrappedBadgeList = useMemo(() => {
      return badgeList.map((badge) => ({
        ...badge,
        handleDelete: () => {
          badge.handleDelete();
          setOpen(false);
        },
      }));
    }, [badgeList]);

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setOpen(false);
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const isFirstRender = useIsFirstRender();
    useEffect(() => {
      if (isFirstRender) return;
      if (search.length > 0) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    }, [search, isFirstRender]);

    return (
      <Popover open={open}>
        <PopoverAnchor
          ref={anchorRef}
          id={id}
          onClick={handlePopoverAnchorClick}
          onMouseDown={handlePopoverAnchorMouseDown}
          onFocus={() => setOpen(true)}
          role="search"
          tabIndex={0}
          className={cn(
            'flex flex-row justify-between items-center p-2 w-full border border-border rounded-sm shadow-sm bg-white min-h-12',
            'active:ring-2 focus:ring-2 focus-within:ring-2 ring-primary focus-visible:ring-primary',
            'cursor-pointer',
            className,
          )}
          data-testid="autone-scope-dropdown-anchor"
        >
          <div className="inline-flex flex-row gap-1 items-center flex-wrap grow max-w-[90%]">
            <AutoneScopeDropdownBadges
              ref={badgeRefs}
              badgeList={wrappedBadgeList}
              onClearAll={() => {
                onClearAll();
                setOpen(false);
              }}
            />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              size={search.length || common('search-placeholder').length || 1}
              className="ps-1 text-sm min-w-0 grow field-sizing-content outline-none placeholder:text-muted-foreground"
              placeholder={common('search-placeholder')}
              data-testid="autone-scope-dropdown-input"
            />
          </div>
          <button
            onClick={(e) => {
              inputRef.current?.focus();
              setOpen(!open);
              e.stopPropagation();
            }}
            onFocus={(e) => e.stopPropagation()}
            className={cn(
              'shrink-0 transition-transform p-0.5 aspect-square rounded-full h-fit hover:bg-muted',
              open && 'rotate-180',
            )}
            type="button"
            data-testid="autone-scope-dropdown-chevron"
          >
            <ChevronDown size={14} className="text-muted-foreground" />
          </button>
        </PopoverAnchor>
        <PopoverContent
          side="bottom"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          onClick={(e) => e.stopPropagation()}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={handlePointerDownOutside}
          className="flex flex-col w-[var(--radix-popover-trigger-width)] h-fit"
          data-testid="autone-scope-dropdown-content"
        >
          {dropdownContent}
        </PopoverContent>
      </Popover>
    );
  },
);

AutoneScopeDropdown.displayName = 'AutoneScopeDropdown';
